import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import BackButton from '../components/BackButton.jsx';
import { useState, useMemo } from 'react';
import clsx from 'clsx';
import { AreaChart } from 'foundry-charts';
import 'foundry-charts/styles';
import api from '../api/client.js';
import FollowupEditor from '../components/FollowupEditor.jsx';
import LogOutreachModal from '../components/LogOutreachModal.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import SalesHistory from '../components/SalesHistory.jsx';
import CustomerScorecard from '../components/CustomerScorecard.jsx';
import ResearchBrief from '../components/ResearchBrief.jsx';

const TOUCH_ICON = {
  visit: '🚗', call: '📞', email: '✉️', quote: '💲', photo: '📷', note: '✏️',
};
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const NAVY = '#0A2240', GOLD = '#D4A017';

function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}
function money(n) {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}k`;
  return `$${Math.round(v)}`;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const { user, hasPermission } = useAuth();
  const [creatingFollowup, setCreatingFollowup] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [editingProspect, setEditingProspect] = useState(false);
  const [prospectForm, setProspectForm] = useState(null);
  const [tab, setTab] = useState('activity');
  const [touchFilter, setTouchFilter] = useState('');
  const [loggingOutreach, setLoggingOutreach] = useState(false);
  const qc = useQueryClient();
  const canSeeSales = user?.role === 'superadmin' || user?.can_see_sales;

  const c = useQuery({
    queryKey: ['customer', id],
    queryFn: () => api.get(`/customers/${id}`).then(r => r.data.data),
  });
  const a = useQuery({
    queryKey: ['customer-activity', id],
    queryFn: () => api.get(`/customers/${id}/activity`).then(r => r.data.data),
  });
  // Shared with SalesHistory (same key) → one fetch; powers the trend chart.
  const sales = useQuery({
    queryKey: ['sales-history', id],
    queryFn: () => api.get(`/sales/customers/${id}/history`).then(r => r.data.data),
    enabled: canSeeSales,
    retry: false,
  });
  // Open pipeline for this customer (KPI + Pipeline tab).
  const pipeline = useQuery({
    queryKey: ['customer-pipeline', id],
    queryFn: () => api.get(`/opportunities?customer_id=${id}&status=open`).then(r => r.data.data),
    enabled: canSeeSales,
    retry: false,
  });
  // Won/lost deals for this customer — separate from `pipeline` above so the
  // open-value KPI stays open-only; shown as its own list in the Pipeline tab
  // instead of just vanishing once a deal closes.
  const closedPipeline = useQuery({
    queryKey: ['customer-pipeline-closed', id],
    queryFn: () => api.get(`/opportunities?customer_id=${id}&status=closed`).then(r => r.data.data),
    enabled: canSeeSales && tab === 'pipeline',
    retry: false,
  });
  // Open / backordered order lines (current D3 snapshot): Orders tab + tab count.
  const openLines = useQuery({
    queryKey: ['customer-open-lines', id],
    queryFn: () => api.get(`/customers/${id}/open-order-lines`).then(r => r.data.data),
    enabled: canSeeSales,
    retry: false,
  });
  // Recent sales orders: Orders tab only.
  const recentOrders = useQuery({
    queryKey: ['customer-orders', id],
    queryFn: () => api.get(`/customers/${id}/orders?limit=25`).then(r => r.data.data),
    enabled: canSeeSales && tab === 'orders',
    retry: false,
  });

  const saveNotes = useMutation({
    mutationFn: (notes) => api.put(`/customers/${id}`, { notes }).then(r => r.data.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customer', id] }); setEditingNotes(false); },
  });

  // Prospects do not come from D3, so unlike real customers their name +
  // contact fields are editable by anyone — no can_edit_customers gate,
  // matching the server rule (customers.js PUT /:id).
  const saveProspect = useMutation({
    mutationFn: (data) => api.put(`/customers/${id}`, data).then(r => r.data.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customer', id] }); setEditingProspect(false); },
  });

  // 24-month sales + GP trend.
  const trend = useMemo(() => {
    const monthly = sales.data?.monthly;
    if (!monthly?.length) return [];
    return [...monthly]
      .sort((x, y) => (x.year - y.year) || (x.month - y.month))
      .slice(-24)
      .map(m => ({
        month: `${MONTH_ABBR[m.month - 1]} ${String(m.year).slice(2)}`,
        Sales: Number(m.sales || 0),
        GP: Number(m.gp || 0),
      }));
  }, [sales.data]);

  if (c.isLoading) return <div className="text-muted">Loading…</div>;
  if (!c.data) return <div className="text-crimson">Not found.</div>;

  const cust = c.data;
  const primaryContact = cust.contacts?.find(p => p.is_primary) || cust.contacts?.[0] || null;
  const stats = a.data?.stats || {};
  const lastTouchDays = daysSince(stats.last_touch_at);
  const lastVisitDays = daysSince(stats.last_visit_at);
  const openPipeValue = (pipeline.data || []).reduce((s, o) => s + Number(o.value_usd || 0), 0);

  const notes = a.data?.notes || [];
  const filteredNotes = touchFilter ? notes.filter(n => (n.touch_type || 'note') === touchFilter) : notes;

  const TABS = [
    { key: 'activity', label: `Activity (${notes.length})` },
    ...(canSeeSales ? [{ key: 'sales', label: 'Sales' }] : []),
    ...(canSeeSales ? [{ key: 'pipeline', label: `Pipeline (${pipeline.data?.length ?? 0})` }] : []),
    ...(canSeeSales ? [{ key: 'orders', label: `Open orders (${openLines.data?.length ?? 0})` }] : []),
    { key: 'followups', label: `Follow-ups (${a.data?.followups.length ?? 0})` },
    { key: 'people', label: `People (${cust.contacts?.length ?? 0})` },
  ];

  return (
    <div className="space-y-4">
      <BackButton fallback="/customers" />
      {/* ── Header ───────────────────────────────────────────── */}
      <div>
        <div className="section-eyebrow">Customer</div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="text-2xl">{cust.name}</h1>
          {cust.status && (
            <span className={clsx('text-[10px] uppercase tracking-wider px-2 py-0.5 rounded',
              cust.status === 'active' ? 'bg-emerald-100 text-emerald-900'
              : cust.status === 'prospect' ? 'bg-amber-100 text-amber-900'
              : 'bg-panel text-muted')}>
              {cust.status}
            </span>
          )}
          {cust.status === 'prospect' && !editingProspect && (
            <button className="text-xs text-navy hover:text-crimson"
                    onClick={() => {
                      setProspectForm({
                        name: cust.name || '', phone: cust.phone || '', email: cust.email || '',
                        address: cust.address || '', city: cust.city || '', state: cust.state || '', zip: cust.zip || '',
                      });
                      setEditingProspect(true);
                    }}>
              ✎ Edit
            </button>
          )}
        </div>
        <div className="rule-crimson" />

        {editingProspect ? (
          <div className="mt-2 card p-3 space-y-2 border-amber-300 bg-amber-50 max-w-md">
            <input className="input text-sm" placeholder="Name" value={prospectForm.name}
                   onChange={e => setProspectForm(f => ({ ...f, name: e.target.value }))} />
            <div className="grid grid-cols-2 gap-2">
              <input className="input text-sm" placeholder="Phone" value={prospectForm.phone}
                     onChange={e => setProspectForm(f => ({ ...f, phone: e.target.value }))} />
              <input className="input text-sm" placeholder="Email" value={prospectForm.email}
                     onChange={e => setProspectForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <input className="input text-sm" placeholder="Address" value={prospectForm.address}
                   onChange={e => setProspectForm(f => ({ ...f, address: e.target.value }))} />
            <div className="grid grid-cols-3 gap-2">
              <input className="input text-sm" placeholder="City" value={prospectForm.city}
                     onChange={e => setProspectForm(f => ({ ...f, city: e.target.value }))} />
              <input className="input text-sm" placeholder="State" value={prospectForm.state}
                     onChange={e => setProspectForm(f => ({ ...f, state: e.target.value }))} />
              <input className="input text-sm" placeholder="Zip" value={prospectForm.zip}
                     onChange={e => setProspectForm(f => ({ ...f, zip: e.target.value }))} />
            </div>
            {saveProspect.isError && (
              <div className="text-crimson text-xs">{saveProspect.error?.response?.data?.error || 'Save failed'}</div>
            )}
            <div className="flex gap-2">
              <button className="btn-primary text-sm" disabled={!prospectForm.name.trim() || saveProspect.isPending}
                      onClick={() => saveProspect.mutate(prospectForm)}>
                {saveProspect.isPending ? 'Saving…' : 'Save'}
              </button>
              <button className="btn-ghost text-sm" onClick={() => setEditingProspect(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted flex flex-wrap gap-x-3">
            <span>{[cust.address, cust.city, cust.state, cust.zip].filter(Boolean).join(', ') || '—'}</span>
            {cust.account_number && <span>· #{cust.account_number}</span>}
            {cust.phone && <span>· 📞 {cust.phone}</span>}
            {cust.rep_name && <span>· Rep: {cust.rep_name}</span>}
            {cust.account_type && cust.account_type !== 'regular' && <span>· {cust.account_type} account</span>}
            {cust.date_opened && <span>· customer since {new Date(cust.date_opened).toLocaleDateString()}</span>}
          </div>
        )}
        {primaryContact && (
          <div className="text-sm text-navy mt-1">
            👤 {primaryContact.name}
            {primaryContact.role && <span className="text-muted"> · {primaryContact.role}</span>}
            {primaryContact.phone && <span className="text-muted"> · {primaryContact.phone}</span>}
          </div>
        )}
      </div>

      {/* ── Action buttons ───────────────────────────────────── */}
      <div className="flex gap-2 flex-wrap">
        <Link to={`/notes/new?customer_id=${cust.id}&touch_type=visit`} className="btn-primary">🚗 Log visit</Link>
        <button className="btn-ghost" onClick={() => setLoggingOutreach(true)}>📞 Log outreach</button>
        <Link to={`/notes/new?customer_id=${cust.id}&touch_type=call`} className="btn-ghost">📞 Log call</Link>
        <Link to={`/notes/new?customer_id=${cust.id}&touch_type=email`} className="btn-ghost">✉️ Log email</Link>
        <Link to={`/notes/new?customer_id=${cust.id}`} className="btn-ghost">+ New note</Link>
        <button className="btn-ghost" onClick={() => setCreatingFollowup(true)}>⏱ + Follow-up</button>
      </div>

      {creatingFollowup && (
        <FollowupEditor initial={{ customer_id: cust.id }}
          onClose={() => setCreatingFollowup(false)}
          onSaved={() => { setCreatingFollowup(false); qc.invalidateQueries({ queryKey: ['customer-activity', id] }); }} />
      )}

      {loggingOutreach && (
        <LogOutreachModal
          customerId={cust.id}
          customerName={cust.name}
          defaultContactName={primaryContact?.name}
          onClose={() => setLoggingOutreach(false)}
          onLogged={() => qc.invalidateQueries({ queryKey: ['customer-activity', id] })}
        />
      )}

      {/* ── Dashboard band ───────────────────────────────────── */}
      {/* Relationship KPIs (always visible) */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        <Stat label="Last touch" value={lastTouchDays != null ? `${lastTouchDays}d` : 'never'}
              warn={lastTouchDays != null && lastTouchDays > 30} />
        <Stat label="Last visit" value={lastVisitDays != null ? `${lastVisitDays}d` : 'never'}
              warn={lastVisitDays != null && lastVisitDays > 60} />
        <Stat label="Total visits" value={stats.total_visits || 0} />
        <Stat label="30d touches" value={stats.touches_30d || 0} />
        {canSeeSales
          ? <Stat label="Open pipeline" value={pipeline.data?.length ? money(openPipeValue) : '—'} />
          : <Stat label="Total touches" value={stats.total_touches || 0} />}
      </div>

      {/* Sales scorecard: leads with the over/under verdict */}
      {canSeeSales && <CustomerScorecard customerId={id} />}

      {/* 24-month sales + GP trend */}
      {canSeeSales && trend.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">Sales and GP: last 24 months</div>
          <div className="rule-crimson" />
          <div className="mt-3">
            <AreaChart
              data={trend}
              xKey="month"
              series={[
                { key: 'Sales', label: 'Sales', color: NAVY },
                { key: 'GP',    label: 'GP',    color: GOLD },
              ]}
              variant="overlapping"
              height={240}
              formatY={money}
            />
          </div>
        </section>
      )}

      {canSeeSales && <ResearchBrief customerId={id} />}

      {/* ── Tabbed drill-in ──────────────────────────────────── */}
      <div className="border-b border-navy-100 flex gap-1 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={clsx('px-3 py-2 text-sm -mb-px border-b-2',
              tab === t.key ? 'border-crimson text-crimson font-medium' : 'border-transparent text-navy hover:bg-panel')}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Activity / Notes & Visits */}
      {tab === 'activity' && (
        <section className="space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {['', 'visit', 'call', 'email', 'quote', 'note'].map(f => (
              <button key={f || 'all'} onClick={() => setTouchFilter(f)}
                className={clsx('px-2.5 py-1 text-xs rounded',
                  touchFilter === f ? 'bg-navy text-white' : 'bg-panel text-navy')}>
                {f ? `${TOUCH_ICON[f]} ${f}` : 'All'}
              </button>
            ))}
          </div>
          {a.isLoading && <div className="text-muted text-sm">Loading…</div>}
          {!a.isLoading && filteredNotes.length === 0 && (
            <div className="card p-4 text-muted text-sm">No {touchFilter || ''} activity yet.</div>
          )}
          {filteredNotes.map(n => (
            <Link key={n.id} to={`/notes/${n.id}`} className="block card p-3 hover:bg-panel">
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-medium text-sm flex items-center flex-wrap gap-1">
                  <span>{TOUCH_ICON[n.touch_type] || '✏️'}</span>
                  <span>{n.touch_type || 'note'} · by {n.user_name}</span>
                  {n.visibility === 'private' && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 text-[10px] uppercase tracking-wide">🔒 Private</span>
                  )}
                </div>
                <div className="text-xs text-muted">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              {n.transcript && (
                <div className="text-sm text-muted mt-1 line-clamp-3">
                  {n.structured?.summary || n.transcript.slice(0, 200)}
                </div>
              )}
              {n.attachment_count > 0 && (
                <div className="text-xs text-muted mt-1">📎 {n.attachment_count} attachment{n.attachment_count === 1 ? '' : 's'}</div>
              )}
            </Link>
          ))}
        </section>
      )}

      {/* Sales */}
      {tab === 'sales' && canSeeSales && <SalesHistory customerId={id} />}

      {/* Pipeline */}
      {tab === 'pipeline' && canSeeSales && (
        <section className="space-y-2">
          {pipeline.isLoading && <div className="text-muted text-sm">Loading…</div>}
          {!pipeline.isLoading && (pipeline.data?.length ?? 0) === 0 && (
            <div className="card p-4 text-muted text-sm">No open opportunities for this customer.</div>
          )}
          {pipeline.data?.map(o => (
            <Link key={o.id} to={`/pipeline/${o.id}`} className="block card p-3 hover:bg-panel">
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-medium text-sm">{o.title || o.product || '(opportunity)'}</div>
                <div className="text-sm font-semibold tabular-nums">{o.value_usd ? money(o.value_usd) : '—'}</div>
              </div>
              <div className="text-xs text-muted mt-1">
                {[o.domain, o.stage, o.expected_close_date ? `close ${new Date(o.expected_close_date).toLocaleDateString()}` : null]
                  .filter(Boolean).join(' · ')}
                {o.user_name && <span> · {o.user_name}</span>}
              </div>
            </Link>
          ))}

          {/* Won/lost — closed deals used to just disappear once a deal's
              stage moved to won or lost since this tab only fetched status=open. */}
          {!closedPipeline.isLoading && (closedPipeline.data?.length ?? 0) > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-wider text-muted pt-2">
                Won / Lost ({closedPipeline.data.length})
              </div>
              {closedPipeline.data.map(o => (
                <Link key={o.id} to={`/pipeline/${o.id}`} className="block card p-3 hover:bg-panel">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-medium text-sm">{o.title || o.product || '(opportunity)'}</div>
                    <div className="text-sm font-semibold tabular-nums">{o.value_usd ? money(o.value_usd) : '—'}</div>
                  </div>
                  <div className="text-xs text-muted mt-1">
                    <span className={clsx('font-medium', o.stage === 'won' ? 'text-emerald-700' : 'text-crimson')}>
                      {o.stage === 'won' ? 'Won' : 'Lost'}
                    </span>
                    {o.domain && <span> · {o.domain}</span>}
                    {o.closed_at && <span> · {new Date(o.closed_at).toLocaleDateString()}</span>}
                    {o.stage === 'lost' && o.lost_reason && <span> · {o.lost_reason}</span>}
                    {o.user_name && <span> · {o.user_name}</span>}
                  </div>
                </Link>
              ))}
            </>
          )}
        </section>
      )}

      {/* Open orders: open / backordered lines + recent orders from D3 */}
      {tab === 'orders' && canSeeSales && (
        <OrdersTab openLines={openLines} recentOrders={recentOrders} />
      )}

      {/* Follow-ups */}
      {tab === 'followups' && (
        <section className="space-y-2">
          <button className="btn-ghost text-sm" onClick={() => setCreatingFollowup(true)}>⏱ + New follow-up</button>
          {(a.data?.followups.length ?? 0) === 0 && <div className="card p-4 text-muted text-sm">None open.</div>}
          {a.data?.followups.map(f => (
            <div key={f.id} className="card p-3 text-sm flex items-baseline justify-between gap-3">
              <div>
                <div className="font-medium">{f.title}</div>
                <div className="text-xs text-muted">{f.status} · {f.urgency} · due {new Date(f.due_date).toLocaleDateString()}</div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* People + customer notes */}
      {tab === 'people' && (
        <section className="space-y-3">
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div className="section-eyebrow">Customer notes</div>
              {hasPermission('can_edit_customers') && !editingNotes && (
                <button className="text-xs text-navy hover:text-crimson"
                        onClick={() => { setNotesValue(cust.notes || ''); setEditingNotes(true); }}>Edit</button>
              )}
            </div>
            <div className="rule-crimson" />
            {editingNotes ? (
              <div className="mt-2 space-y-2">
                <textarea className="input min-h-[80px] text-sm w-full" value={notesValue}
                          onChange={e => setNotesValue(e.target.value)} autoFocus />
                {saveNotes.isError && (
                  <div className="text-crimson text-xs">{saveNotes.error?.response?.data?.error || 'Save failed'}</div>
                )}
                <div className="flex gap-2">
                  <button className="btn-primary text-sm" disabled={saveNotes.isPending}
                          onClick={() => saveNotes.mutate(notesValue)}>{saveNotes.isPending ? 'Saving…' : 'Save'}</button>
                  <button className="btn-ghost text-sm" onClick={() => setEditingNotes(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="text-sm mt-2 whitespace-pre-wrap">{cust.notes || '—'}</div>
            )}
          </div>

          {cust.contacts?.length > 0 ? cust.contacts.map(p => (
            <div key={p.id} className="card p-3">
              <div className="font-medium">{p.name} {p.is_primary && <span className="text-crimson text-xs">★</span>}</div>
              <div className="text-xs text-muted">{p.role}</div>
              <div className="text-sm mt-1">{[p.phone, p.email].filter(Boolean).join(' · ') || '—'}</div>
            </div>
          )) : <div className="card p-4 text-muted text-sm">No contacts on file.</div>}
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div className={`card p-2 text-center ${warn ? 'ring-1 ring-crimson' : ''}`}>
      <div className={`text-lg font-semibold ${warn ? 'text-crimson' : 'text-navy'}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted">{label}</div>
    </div>
  );
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString() : '—';
}
function fmtQty(n) {
  const v = Number(n || 0);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}
function fmtUsd(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function OrdersTab({ openLines, recentOrders }) {
  const lines = openLines.data || [];
  const orders = recentOrders.data || [];
  const openValue = lines.reduce((s, l) => s + Number(l.line_amount || 0), 0);
  const asOf = lines.find(l => l.as_of)?.as_of;
  return (
    <section className="space-y-3">
      <div className="card p-3">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <div className="section-eyebrow">Open and backordered lines</div>
          {lines.length > 0 && (
            <div className="text-xs text-muted">
              {lines.length} line{lines.length === 1 ? '' : 's'} · {fmtUsd(openValue)}
              {asOf && <> · as of {fmtDate(asOf)}</>}
            </div>
          )}
        </div>
        <div className="rule-crimson" />
        {openLines.isLoading && <div className="text-muted text-sm mt-2">Loading…</div>}
        {openLines.isError && <div className="text-crimson text-sm mt-2">{openLines.error?.response?.data?.error || 'Failed to load open orders.'}</div>}
        {!openLines.isLoading && !openLines.isError && lines.length === 0 && (
          <div className="text-muted text-sm mt-2">No open or backordered lines for this customer.</div>
        )}
        {lines.length > 0 && (
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs">
              <thead className="text-left text-muted uppercase tracking-wider">
                <tr>
                  <th className="py-1 pr-3">Order</th>
                  <th className="py-1 pr-3 text-right">Line</th>
                  <th className="py-1 pr-3">Part</th>
                  <th className="py-1 pr-3">Description</th>
                  <th className="py-1 pr-3 text-right">Ordered</th>
                  <th className="py-1 pr-3 text-right">Shipped</th>
                  <th className="py-1 pr-3 text-right">Backordered</th>
                  <th className="py-1 pr-3">Promised</th>
                  <th className="py-1 pr-3">Hold</th>
                  <th className="py-1 pr-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.id} className="border-t border-panel">
                    <td className="py-1 pr-3 font-mono text-[11px] whitespace-nowrap">{l.order_number}</td>
                    <td className="py-1 pr-3 text-right">{l.line_number}</td>
                    <td className="py-1 pr-3 font-mono text-[11px] whitespace-nowrap">{l.part_number}</td>
                    <td className="py-1 pr-3">{l.description}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{fmtQty(l.qty_ordered)}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{fmtQty(l.qty_shipped)}</td>
                    <td className={clsx('py-1 pr-3 text-right tabular-nums', Number(l.qty_backordered) > 0 && 'text-crimson font-medium')}>
                      {fmtQty(l.qty_backordered)}
                    </td>
                    <td className="py-1 pr-3 whitespace-nowrap">{fmtDate(l.promised_ship_date)}</td>
                    <td className="py-1 pr-3">{l.hold_reason || '—'}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{fmtUsd(l.line_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card p-3">
        <div className="section-eyebrow">Recent orders</div>
        <div className="rule-crimson" />
        {recentOrders.isLoading && <div className="text-muted text-sm mt-2">Loading…</div>}
        {recentOrders.isError && <div className="text-crimson text-sm mt-2">{recentOrders.error?.response?.data?.error || 'Failed to load orders.'}</div>}
        {!recentOrders.isLoading && !recentOrders.isError && orders.length === 0 && (
          <div className="text-muted text-sm mt-2">No orders on file for this customer.</div>
        )}
        {orders.length > 0 && (
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs">
              <thead className="text-left text-muted uppercase tracking-wider">
                <tr>
                  <th className="py-1 pr-3">Order</th>
                  <th className="py-1 pr-3">Date</th>
                  <th className="py-1 pr-3">Type</th>
                  <th className="py-1 pr-3">Status</th>
                  <th className="py-1 pr-3">Customer PO</th>
                  <th className="py-1 pr-3">Shipped</th>
                  <th className="py-1 pr-3">Invoice</th>
                  <th className="py-1 pr-3">Salesperson</th>
                  <th className="py-1 pr-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} className="border-t border-panel">
                    <td className="py-1 pr-3 font-mono text-[11px] whitespace-nowrap">{o.order_number}</td>
                    <td className="py-1 pr-3 whitespace-nowrap">{fmtDate(o.order_date)}</td>
                    <td className="py-1 pr-3">{o.order_type}</td>
                    <td className="py-1 pr-3">{o.status}</td>
                    <td className="py-1 pr-3">{o.customer_po || '—'}</td>
                    <td className="py-1 pr-3 whitespace-nowrap">{fmtDate(o.ship_date)}</td>
                    <td className="py-1 pr-3 font-mono text-[11px]">{o.invoice_number || '—'}</td>
                    <td className="py-1 pr-3">{o.salesperson_name || o.salesperson_code || '—'}</td>
                    <td className={clsx('py-1 pr-3 text-right tabular-nums', Number(o.order_amount) < 0 && 'text-crimson')}>
                      {fmtUsd(o.order_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
